import { type ComponentProps } from 'react'
import { Slot } from '@radix-ui/react-slot'
import clsx from 'clsx'
import Icon from '@components/utils/Icon'

function Breadcrumb({ ...props }: ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />
}

function BreadcrumbList({ className, ...props }: ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={clsx(
        'tw:text-gray-500 tw:flex tw:flex-wrap tw:items-center tw:gap-1.5 tw:text-sm tw:wrap-break-word tw:sm:gap-2.5',
        className
      )}
      {...props}
    />
  )
}

function BreadcrumbItem({ className, ...props }: ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={clsx('tw:inline-flex tw:items-center tw:gap-1.5', className)}
      {...props}
    />
  )
}

function BreadcrumbLink({
  asChild,
  className,
  children,
  ...props
}: ComponentProps<'a'> & {
  asChild?: boolean
}) {
  if (asChild) {
    return (
      <Slot
        data-slot="breadcrumb-link"
        className={clsx(
          'tw:hover:text-gray-900 tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300 tw:transition-colors',
          className
        )}
      >
        {children}
      </Slot>
    )
  }

  return (
    <a
      data-slot="breadcrumb-link"
      className={clsx(
        'tw:hover:text-gray-900 tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300 tw:transition-colors',
        className
      )}
      {...props}
    >
      {children}
    </a>
  )
}

function BreadcrumbPage({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      aria-current="page"
      className={clsx('tw:font-semibold tw:text-gray-cool-800', className)}
      {...props}
    />
  )
}

function BreadcrumbSeparator({ children, className, ...props }: ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={clsx('tw:[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <Icon type="chevron-right" width={14} height={14} />}
    </li>
  )
}

function BreadcrumbEllipsis({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={clsx('tw:flex tw:size-9 tw:items-center tw:justify-center', className)}
      {...props}
    >
      <Icon type="dots-horizontal" width={16} height={16} />
      <span className="tw:sr-only">More</span>
    </span>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
}
